import clsx from "clsx";
import {
  ChangeEvent,
  InputHTMLAttributes,
  SyntheticEvent,
  useMemo,
  useState,
} from "react";
import { withMask } from "use-mask-input";
import { clsxm } from "@/utils/clsxm";

interface IProps {
  value: string;
  name: string;
  label?: string;
  unit?: string;
  placeholder?: string;
  required?: boolean;
  error?: string | boolean;
  type?: "text" | "phone" | "verifyCode" | "dob" | "email";
  withoutLabel?: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: SyntheticEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}

const InputField = ({
  label,
  value,
  unit,
  name,
  required,
  error,
  type = "text",
  onChange,
  onBlur,
  placeholder,
  withoutLabel = false,
  disabled = false,
}: IProps) => {
  const typeFieldProps: InputHTMLAttributes<HTMLInputElement> = useMemo(() => {
    if (type === "phone") {
      return {
        ref: withMask("(999) 999-9999", {
          jitMasking: true,
          showMaskOnHover: false,
        }),
        inputMode: "tel",
        pattern: "[0-9]*",
      };
    }
    if (type === "verifyCode") {
      return {
        ref: withMask("9999", {
          jitMasking: true,
          showMaskOnHover: false,
        }),
        inputMode: "numeric",
        pattern: "[0-9]*",
      };
    }
    if (type === "dob") {
      return {
        ref: withMask("99/99/9999", {
          jitMasking: true,
          showMaskOnHover: false,
        }),
        inputMode: "numeric",
        pattern: "[0-9]*",
      };
    }
    return {};
  }, [type]);

  if (withoutLabel) {
    return (
      <div className="relative">
        <input
          {...typeFieldProps}
          value={value || ""}
          name={name}
          onChange={onChange}
          onBlur={(e) => {
            onBlur?.(e);
          }}
          disabled={disabled}
          className={clsxm(
            "text-2xl border rounded-full w-full outline-none text-center transition duration-500 focus:border-main-button-v2 min-h-16 sm:min-h-[76px]",
            error ? "border-red" : "border-stroke-v2"
          )}
          required={required}
          placeholder={placeholder || " "}
        />

        {error && typeof error === "string" && (
          <span className="text-left mt-2 text-red text-sm sm:text-base">
            {error}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="relative w-full">
      <label
        className={clsx(
          "text-second-text-v2 text-xs pointer-events-none mb-0.5"
        )}
      >
        {label}
      </label>
      <input
        {...typeFieldProps}
        value={value || ""}
        name={name}
        onChange={onChange}
        onBlur={(e) => {
          onBlur?.(e);
        }}
        type="text"
        disabled={disabled}
        className={clsx(
          "px-5 py-1 w-full border rounded-3xl focus:border-accent-button-v2 outline-none text-sm transition-all min-h-10",
          error ? "border-red" : "border-stroke-v2"
        )}
        required={required}
        placeholder={placeholder || " "}
      />

      {unit && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
          {unit}
        </div>
      )}
      {error && typeof error === "string" && (
        <span className="text-left mt-2 text-red text-sm">{error}</span>
      )}
    </div>
  );
};

export { InputField };
